import React, { useCallback, useEffect } from "react"

import { useTheme, Box } from "@mui/material"
import _ from "lodash"

import { useVenue } from "providers/venue"

import { OrdinalSelector } from "components/OrdinalSelector"
import { VenueSelector } from "components/VenueSelector"
import { FeatureChipToggleBar } from "../../../components/domains/features/ChipToggleBar"
import { PageCard } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { useMap } from "hooks/map/useMap"
import { useHighLightFeaturesMap } from "hooks/feature/useHighLightFeaturesMap"

import { pushDataLayer } from "utils/googletagmanager"
import { GTM_AMENITY_SEARCH } from "constant"
import { pluralize } from "utils/string"
import { SidebarMenu } from "components/navigation/SidebarMenu"
import { MapPageLayout } from "layouts/kiosk-h"
import { usePageSideBarLayout } from "providers/ui/hooks"

export const FeatureByCategoryKioskHPage = ({
  Layout = MapPageLayout,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { venues, isMultiOrdinalVenue, isMultiVenueProject, dataLoaded } =
    useVenue()

  const {
    viewingVenueId,
    viewingVenueLevels,
    quickSearchCategories,
    quickSearchFeatures,
    viewingOrdinal,
    findNearestOrdinal,
    searchParams,
    comingFromOrdinal,
    category,
    locationVenueId,
    locationOrdinal,
    onChangeVenue,
    setSearchParams,
    toggleQuickSearch,
  } = useMap()

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppMap`] || {}
  const {
    searchBarContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    venueSelector,
    ordinalSelectMenu,
    toolbarContainer,
  } = styleOverrides

  const enableMap =
    !_.isNil(viewingOrdinal) && _.isNil(comingFromOrdinal) && dataLoaded

  const onClickOrdinal = useCallback(
    (ordinal) => {
      const currentParams = Object.fromEntries(searchParams.entries())
      setSearchParams({ ...currentParams, ordinal })
    },
    [setSearchParams, searchParams]
  )
  const handleClickQuickSearch = useCallback(
    (type, amenityCategory) => {
      const pluralType = pluralize(type)
      toggleQuickSearch(pluralType, amenityCategory)
    },
    [toggleQuickSearch]
  )

  useEffect(() => {
    if (!viewingVenueId || !dataLoaded) return

    // Use `viewingOrdinal` if available (when switching ordinals on the amenity page).
    if (!_.isNil(viewingOrdinal)) {
      setSearchParams(
        { venue: viewingVenueId, ordinal: viewingOrdinal },
        { replace: true }
      )
      return
    }
    /**
     * Find the nearest ordinal for amenity:
     * - If `comingFromOrdinal` is present, use it (applies when navigating from another page).
     * - If `viewingOrdinal` is missing and `comingFromOrdinal` is absent, but `locationOrdinal` exists, use `locationOrdinal`.
     * - Otherwise, default to 0 (GF).
     */
    const currentOrdinal = comingFromOrdinal || locationOrdinal || 0
    const nearestOrdinal = findNearestOrdinal(currentOrdinal)?.toString()
    setSearchParams(
      { venue: viewingVenueId, ordinal: nearestOrdinal },
      { replace: true }
    )
  }, [
    dataLoaded,
    category,
    comingFromOrdinal,
    viewingVenueId,
    viewingOrdinal,
    findNearestOrdinal,
    locationOrdinal,
    setSearchParams,
  ])

  //@ts-ignore
  useHighLightFeaturesMap(quickSearchFeatures, Number(viewingOrdinal), {
    enabled: enableMap,
  })

  useEffect(() => {
    if (category) {
      pushDataLayer({
        event: GTM_AMENITY_SEARCH,
        amenity_category: category,
      })
    }
  }, [category])

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <Box sx={toolbarContainer}>
        <Box sx={searchBarContainer}>
          {/* TODO: Find better solution for amenity quicksearch search drawer */}
        </Box>

        <Box sx={quickLinkContainer}>
          <FeatureChipToggleBar
            categories={quickSearchCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
      </Box>
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelector
            selectedOrdinal={Number(viewingOrdinal)}
            levels={viewingVenueLevels}
            onClickOrdinal={onClickOrdinal}
            locationOrdinal={locationOrdinal}
          />
        </Box>
      )}

      {isMultiVenueProject && (
        <Box sx={venueSelector}>
          <VenueSelector
            selectedVenue={viewingVenueId}
            venues={venues}
            onClickVenue={onChangeVenue}
            currentVenue={locationVenueId}
          />
        </Box>
      )}
    </Layout>
  )
}
